import React from 'react'
import {
  Button,
  Modal,
  ModalHeader,
  ModalContent,
  ModalActions,
  Text,
  Box,
} from '@lightspeed/design-system-react'
import classNames from 'classnames'

import { T, useT } from '@transifex/react'
import { StepBreadcrumbs } from '../StepBreadcrumbs'
import { STEPS } from '../types'
import {
  CONTACT_OPTIONS,
  CustomerInfo,
  CUSTOMER_NAME_FIELD,
} from '../../../common/types'
import {
  getDefaultContactOption,
  hasContactInfo,
  isInvalidEmail,
} from '../../../common/customer-contact-utils'
import { ContactNumber } from '../../../common/ContactNumber'
import { FulfilmentType } from '../../../../../../models/fulfilment'

interface Props {
  onStepChange: (step: STEPS) => void
  onClose: () => void
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  type: FulfilmentType
  newCustomer: CustomerInfo | null
}

const shouldDisableNext = (customer: CustomerInfo | null) => {
  const name: string[] = []
  let hasAllNeededCustomerInfo = false
  if (customer) {
    Object.values(CUSTOMER_NAME_FIELD).forEach(option => {
      const value = customer[option]
      if (value && value.trim()) {
        name.push(option)
      }
    })
    if (hasContactInfo(customer) && name.length > 0) {
      hasAllNeededCustomerInfo = true
    }
  }

  return !hasAllNeededCustomerInfo || isInvalidEmail(customer)
}

export const Step2Add: React.FC<Props> = ({
  onStepChange,
  onClose,
  onChange,
  type,
  newCustomer,
}) => {
  const t = useT()

  return (
    <Modal onClose={onClose}>
      <StepBreadcrumbs
        step={STEPS.ADD}
        isAddingCustomer={Boolean(newCustomer)}
      />
      <ModalHeader>
        {type === FulfilmentType.DELIVERY ? (
          <T
            _str="Contact info for the delivery"
            _context="Mark all as unfulfilled modal"
          />
        ) : (
          <T
            _str="Contact info for the pickup"
            _context="Mark all as unfulfilled modal"
          />
        )}
        <Text tag="p" className="vd-mt2">
          <T _str="Enter details to contact the customer about this order." />
        </Text>
      </ModalHeader>
      <ModalContent>
        <>
          <div className="vd-g-row">
            <div className="vd-g-col vd-g-xs-12 vd-g-s-6 vd-pl1 vd-pr1">
              <div className="vd-text-label vd-mb2 vd-mt2">
                <T
                  _str="First name"
                  _context="Mark as unfulfilled modal - first name label"
                />
              </div>
              <div className="vd-value">
                <input
                  className="vd-input"
                  placeholder={t('Enter first name', {
                    _context:
                      'Placeholder for first name input in customer step',
                  })}
                  value={newCustomer!.firstName || ''}
                  onChange={onChange}
                  name={CUSTOMER_NAME_FIELD.FIRSTNAME}
                />
              </div>
            </div>
            <div className="vd-g-col vd-g-xs-12 vd-g-s-6 vd-pl1 vd-pr1">
              <div className="vd-text-label vd-mb2 vd-mt2">
                <T
                  _str="Last name"
                  _context="Mark as unfulfilled modal - last name label"
                />
              </div>
              <div className="vd-value">
                <input
                  className="vd-input"
                  placeholder={t('Enter last name', {
                    _context:
                      'Placeholder for last name input in customer step',
                  })}
                  value={newCustomer!.lastName || ''}
                  onChange={onChange}
                  name={CUSTOMER_NAME_FIELD.LASTNAME}
                />
              </div>
            </div>
          </div>
          <div className="vd-g-row vd-mt4">
            <div className="vd-g-col vd-g-xs-12 vd-g-s-6 vd-pl1 vd-pr1">
              <div className="vd-text-label vd-mb2 vd-mt2">
                <T
                  _str="Contact number"
                  _context="Mark as unfulfilled modal - contact number label"
                />
              </div>
              <ContactNumber
                customer={newCustomer}
                onChange={onChange}
                defaultOption={getDefaultContactOption(newCustomer)}
              />
            </div>
            <div className="vd-g-col vd-g-xs-12 vd-g-s-6 vd-pl1 vd-pr1">
              <div className="vd-text-label vd-mb2 vd-mt2">
                <T
                  _str="Email"
                  _context="Mark as unfulfilled modal - email label"
                />
              </div>
              <div className="vd-value">
                <input
                  className={classNames('vd-input', {
                    'vd-input--error': isInvalidEmail(newCustomer),
                  })}
                  placeholder={t('Enter email', {
                    _context: 'Placeholder for email input in customer step',
                  })}
                  value={newCustomer!.email || ''}
                  onChange={onChange}
                  name={CONTACT_OPTIONS.EMAIL}
                />
              </div>
            </div>
          </div>
        </>
      </ModalContent>
      <ModalActions>
        <Box className="vd-btn-group">
          <Button
            onClick={() => onStepChange(STEPS.CUSTOMER)}
            variant="supplementary"
            data-track="back-customer-step"
          >
            <T _str="Back" _context="Mark as unfulfilled modal - back button" />
          </Button>
          <Button
            onClick={() => onStepChange(STEPS.DETAILS)}
            disabled={shouldDisableNext(newCustomer)}
            variant="go"
            data-track="next-details-step"
          >
            <T _str="Next" _context="Mark as unfulfilled modal - next button" />
          </Button>
        </Box>
      </ModalActions>
    </Modal>
  )
}

Step2Add.displayName = 'Step2Add'
